import {
  createAsyncThunk,
  createSlice,
  type AnyAction,
  type PayloadAction,
  type ThunkDispatch
} from '@reduxjs/toolkit';
import {
  type BelayUserConnection,
  type ConnectionPageResponse,
  UserConnectionListKey,
  type UserConnection,
  type UserConnectionStatus
} from '@alltrails/types/belay';
import type { LanguageRegionCode } from '@alltrails/types/LanguageRegionCode';
import { addActionsToConnections, getNextHref } from '../utils/belayHelpers';
import { getConnections, getConnectionsNextPage } from '../utils/belayRequests';
import { addUserConnections } from './userConnections';

type ListState = {
  userIds: string[];
  nextHref: string | null;
  isLoading: boolean;
  isError?: boolean;
  totalItemCount: number;
};

type TraditionalUserConnectionListKeys = Exclude<
  UserConnectionListKey,
  | UserConnectionListKey.Reactors
  | UserConnectionListKey.TrailReview
  | UserConnectionListKey.Notifications
  | UserConnectionListKey.Feed
  | UserConnectionListKey.Collaborators
>;

export type UserConnectionListsState = Record<UserConnectionListKey, ListState>;

const initialState: UserConnectionListsState = {
  [UserConnectionListKey.FindFriends]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.FollowerRequests]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Suggested]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Reactors]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Blocked]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Followers]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Following]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Mutual]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.TrailReview]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Notifications]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Feed]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 },
  [UserConnectionListKey.Collaborators]: { userIds: [], nextHref: null, isLoading: true, totalItemCount: 0 }
};

const UserConnectionStatuses: Record<TraditionalUserConnectionListKeys, UserConnectionStatus> = {
  [UserConnectionListKey.FindFriends]: 'contact-of',
  [UserConnectionListKey.FollowerRequests]: 'follow-requested-by',
  [UserConnectionListKey.Suggested]: 'suggested-follow',
  [UserConnectionListKey.Blocked]: 'blocking',
  [UserConnectionListKey.Followers]: 'followed-by',
  [UserConnectionListKey.Following]: 'following',
  [UserConnectionListKey.Mutual]: 'mutual'
};

const handleNewConnectionPageResponse = (
  data: ConnectionPageResponse,
  dispatch: ThunkDispatch<unknown, unknown, AnyAction>
) => {
  const userConnections = addActionsToConnections(data.connections);
  dispatch(addUserConnections(userConnections));

  const userIds = userConnections.map(connection => connection.user.id);
  const nextHref = getNextHref(data.links);
  const totalItemCount = data.pageInfo.totalItemCount || data.pageInfo.itemCount;

  return { userIds, nextHref, totalItemCount };
};

export const populateUserConnectionList = createAsyncThunk(
  'userConnectionLists/populateUserConnectionList',
  async (
    {
      listKey,
      userId
    }: {
      listKey: TraditionalUserConnectionListKeys;
      userId?: number;
      languageRegionCode: LanguageRegionCode;
    },
    { dispatch }
  ) => {
    if (!userId) return { userIds: [], nextHref: null, totalItemCount: 0 };

    const data = await getConnections(UserConnectionStatuses[listKey], userId);
    return handleNewConnectionPageResponse(data, dispatch);
  }
);

export const getUserConnectionListNextPage = createAsyncThunk(
  'userConnectionLists/getUserConnectionListNextPage',
  async (
    { listKey }: { listKey: UserConnectionListKey; languageRegionCode: LanguageRegionCode },
    { dispatch, getState }
  ) => {
    const state = getState() as { userConnectionLists: UserConnectionListsState };
    const { nextHref } = state.userConnectionLists[listKey];

    if (!nextHref) return { userIds: [], nextHref: null, totalItemCount: 0 };

    dispatch({
      type: 'userConnectionLists/setNextHref',
      payload: { listKey, nextHref: null }
    });

    const data = await getConnectionsNextPage(nextHref);
    return handleNewConnectionPageResponse(data, dispatch);
  }
);

export const populateUserConnectionListWithConnections = createAsyncThunk(
  'userConnectionLists/populateUserConnectionListWithConnections',
  async (
    {
      connections,
      listKey,
      nextHref
    }: {
      connections: BelayUserConnection[];
      listKey:
        | UserConnectionListKey.Reactors
        | UserConnectionListKey.TrailReview
        | UserConnectionListKey.Notifications
        | UserConnectionListKey.Feed
        | UserConnectionListKey.Collaborators;
      nextHref?: string;
    },
    { dispatch }
  ) => {
    const userConnections = addActionsToConnections(connections);
    dispatch(addUserConnections(userConnections));
    return {
      listKey,
      ids: userConnections.map(connection => connection.user.id),
      nextHref
    };
  }
);

export const addUserConnectionsToList = createAsyncThunk(
  'userConnectionLists/addUserConnectionsToList',
  async (
    {
      connections,
      listKey,
      nextHref
    }: {
      connections: BelayUserConnection[];
      listKey:
        | UserConnectionListKey.Reactors
        | UserConnectionListKey.TrailReview
        | UserConnectionListKey.Notifications
        | UserConnectionListKey.Feed
        | UserConnectionListKey.Collaborators;
      nextHref?: string;
    },
    { dispatch }
  ) => {
    const userConnections = addActionsToConnections(connections);
    dispatch(addUserConnections(userConnections));
    return {
      listKey,
      ids: userConnections.map(connection => connection.user.id),
      nextHref
    };
  }
);

export const userConnectionListsSlice = createSlice({
  name: 'userConnectionLists',
  initialState,
  reducers: {
    removeUserConnection: (
      state,
      { payload }: PayloadAction<{ userConnection: UserConnection; listKey: UserConnectionListKey }>
    ) => {
      const { userConnection, listKey } = payload;
      const index = state[listKey].userIds.findIndex(id => id === userConnection.user.id);
      if (index !== -1) {
        state[listKey].userIds.splice(index, 1);
      }
      return state;
    },
    clearUserConnections: (state, { payload }: PayloadAction<UserConnectionListKey>) => {
      state[payload].userIds = [];
      state[payload].isLoading = true;
      return state;
    },
    setErrorState: (
      state,
      { payload }: PayloadAction<{ isError: boolean; listKey: UserConnectionListKey }>
    ) => {
      state[payload.listKey].isError = payload.isError;
      return state;
    },
    setNextHref: (
      state,
      { payload }: PayloadAction<{ nextHref: string | null; listKey: UserConnectionListKey }>
    ) => {
      state[payload.listKey].nextHref = payload.nextHref;
      return state;
    }
  },
  extraReducers: builder => {
    builder.addCase(populateUserConnectionList.fulfilled, (state, { payload, meta }) => {
      const { listKey } = meta.arg;
      const { userIds, nextHref, totalItemCount } = payload;
      state[listKey].userIds = userIds;
      state[listKey].nextHref = nextHref;
      state[listKey].isLoading = false;
      state[listKey].isError = false;
      state[listKey].totalItemCount = totalItemCount;
      return state;
    });
    builder.addCase(populateUserConnectionList.rejected, (state, { meta }) => {
      const { listKey } = meta.arg;
      state[listKey].isLoading = false;
      state[listKey].isError = true;
      return state;
    });
    builder.addCase(getUserConnectionListNextPage.fulfilled, (state, { payload, meta }) => {
      const { listKey } = meta.arg;
      state[listKey].userIds = [...state[listKey].userIds, ...payload.userIds];
      state[listKey].nextHref = payload.nextHref;
      return state;
    });
    builder.addCase(populateUserConnectionListWithConnections.fulfilled, (state, { payload }) => {
      state[payload.listKey].userIds = payload.ids;
      state[payload.listKey].isLoading = false;
      state[payload.listKey].nextHref = payload.nextHref || null;
      return state;
    });
    builder.addCase(addUserConnectionsToList.fulfilled, (state, { payload }) => {
      state[payload.listKey].userIds = [...state[payload.listKey].userIds, ...payload.ids];
      state[payload.listKey].isLoading = false;
      state[payload.listKey].nextHref = payload.nextHref || null;
      return state;
    });
  }
});

export const {
  clearUserConnections,
  removeUserConnection,
  setErrorState,
  setNextHref
} = userConnectionListsSlice.actions;

export default userConnectionListsSlice.reducer;
